import { iGV_Database } from "src/app/shared/database/database";
import { Injectable } from "@angular/core";
import { IndexedDBService } from "./indexed-db.service";

@Injectable({
  providedIn: "root",
})
/**
 * Service for managing PEA New Load Fuse data in IndexedDB,
 *  *
 * Extends the {@link IndexedDBService} to provide additional methods for handling
 * new fuse data, including saving new load fuse results with associated metadata.
 *
 * The data is to be cached for 24 hours, after which it should be considered stale and
 * removed from the cache.
 *
 * @remarks
 * This service is intended to be used within the application's dependency injection
 * system and relies on injected instances of Store and ExtensionService.
 */
export class IndexedDBNewFuseService extends IndexedDBService {
  constructor() {
    super();
  }

  /**
   * Stores new fuse trace data in IndexedDB with a composite key based on parameters needed by the
   * PEA New Load Fuse API.
   *
   * @param assetId - The unique identifier of the asset (Oh Primary Conductor) selected for the calculation
   * @param tr_type - The transformer type selected for the calculation
   * @param kva - The KVA (kilovolt-ampere) rating selected for the calculation
   * @param phasing - The phasing information (e.g., 'A', 'B', 'C', 'ABC') selected for the calculation
   * @param circuit_id - The identifier of the circuit associated with the asset.
   * @param data - The trace data object returned from the API to be stored.
   */

  /**
   * Stores new fuse trace data in IndexedDB with a string key.
   * @param key - The unique string key for the data
   * @param data - The trace data object returned from the API to be stored.
   */
  public async saveNewFuseData(key: string, data: any): Promise<void> {
    try {
      const timestamp = new Date();
      await iGV_Database.newFuseData.put({ newFuseKey: key, data, timestamp });
    } catch (error) {
      console.error("Error saving newFuse data to IndexedDB with key: " + key + ": ", error);
    }
  }
  /**
   * Gets new fuse data from IndexedDB if it exists and is not older than 24 hours.
   * If the data is older than 24 hours, deletes the entry and returns null.
   * @param assetId - The unique identifier of the asset
   * @param tr_type - The transformer type
   * @param kva - The KVA rating
   * @param phasing - The phasing information
   * @param circuit_id - The circuit identifier
   * @returns The cached data if valid, or null
   */
  /**
   * Gets new fuse data from IndexedDB by string key if it exists and is not older than 24 hours.
   * If the data is older than 24 hours, deletes the entry and returns null.
   * @param key - The unique string key for the data
   * @returns The cached data if valid, or null
   */
  public async getNewFuseData(key: string): Promise<any | null> {
    try {
      const entry = await iGV_Database.newFuseData.get(key);
      if (!entry) {
        return null;
      }
      if (this.isTimestampValid(entry.timestamp)) {
        return entry.data;
      } else {
        await this.deleteNewFuseData(key);
        return null;
      }
    } catch (error) {
      console.error("Error fetching newFuse data from IndexedDB:", error);
      return null;
    }
  }

  /**
   * Checks if a timestamp is within the last 24 hours.
   * @param timestamp - The timestamp to check (Date or string)
   * @returns true if within 24 hours, false otherwise
   */
  private isTimestampValid(timestamp: Date | string): boolean {
    if (!timestamp) return false;
    const now = new Date().getTime();
    const ts = timestamp instanceof Date ? timestamp.getTime() : new Date(timestamp).getTime();
    const diff = now - ts;
    return diff <= 24 * 60 * 60 * 1000; // 24 hours in ms
  }

  /**
   * Deletes a newFuseData entry by key.
   * @param newFuseKey - The composite key
   */
  private async deleteNewFuseData(newFuseKey: string): Promise<void> {
    try {
      await iGV_Database.newFuseData.delete(newFuseKey);
    } catch (error) {
      console.error("Error deleting stale newFuse data from IndexedDB:", error);
    }
  }
}
